import React, { useMemo } from 'react';
import { accent } from '../helpers';
import { useSalleResultat } from '../../../../hooks/useSalle';

/**
 * Template "salle-questions" — les questions de la salle, rendues à la salle.
 *
 * Voir docs/conferences/seminaire-RETOURS-2026-09-22.md (R9). Elle vient juste
 * après « La salle » (slide 34), où Adrien vient d'expliquer qu'une IA a lu,
 * classé et regroupé les questions pendant qu'il parlait. Cette slide-ci est
 * la pièce à conviction : sans elle, l'automatisation est une affirmation de
 * plus.
 *
 * 24/09 — ELLE PROJETTE LES QUESTIONS, PLUS LEURS SUJETS. Retour de la
 * répétition : la slide n'affichait que des sujets (« Impact sur l'emploi »),
 * et la salle n'y retrouvait pas ce qu'elle avait écrit. Le modèle rend
 * maintenant chaque question RÉÉCRITE — orthographe corrigée, phrase
 * complétée —, et ne fusionne que celles qu'une seule phrase peut porter
 * (« Termes à définir : Markdown et IA », « ×2 » devant). Voir
 * `backend/prompts/synthese_salle.md`.
 *
 * ⚠️ CE QU'ELLE PROJETTE N'EST TOUJOURS PAS CE QUE LA SALLE A ÉCRIT. C'est la
 * réécriture du modèle — sans nom de personne, d'équipe ni d'application — et
 * c'est ce qui rend l'affichage montrable (voir `_themes_projetables` dans
 * `backend/salle.py`). Le texte brut ne sort jamais du prompteur. Ne pas
 * « améliorer » cette slide en y affichant les questions telles quelles.
 *
 * ⚠️ ADRIEN LES A LUES AVANT, sur son prompteur, à la slide 34. La règle « on
 * ne projette que ce qu'Adrien a relu » tient donc par l'ORDRE DES SLIDES :
 * ne pas intervertir 35 et 36.
 *
 * ─────────────────────── 5 QUESTIONS PAR CATÉGORIE ───────────────────────
 *
 * Le nombre de questions n'est pas connu à l'écriture de la slide : 3 comme
 * 40. Une question réécrite fait jusqu'à 20 mots, et 40 phrases ne tiennent
 * sur aucun mur. Règle arrêtée par Adrien le 24/09 : chaque catégorie montre
 * ses **5 questions les plus posées** (le backend les trie déjà ainsi), puis
 * une ligne « + N autres questions ». Le prompteur, lui, les liste toutes.
 * Avec 3 catégories et « Autres » au plus, l'écran porte donc 20 lignes au
 * maximum — et la police se resserre au-delà de 8.
 *
 * Champs de slide :
 *  - kicker : petit label au-dessus du titre
 *  - title  : le titre (défaut : "Vos questions")
 *  - accent : "cyan" (défaut) | "red" | "amber" | "violet"
 */

const PAR_CATEGORIE = 5;
const LIGNES_CONFORTABLES = 8;

/** « ×3 Quel outil choisir ? » → le compteur d'un côté, le texte de l'autre. */
function separerCompteur(libelle) {
    const m = /^×(\d+)\s+([\s\S]+)$/.exec(libelle || '');
    return m ? { n: parseInt(m[1], 10), texte: m[2] } : { n: 1, texte: libelle || '' };
}

/** Le « ×3 » d'une question fusionnée, ou rien quand elle n'a été posée qu'une fois. */
function Compteur({ n, ton, petit }) {
    if (!n || n < 2) return null;
    return (
        <span
            className={`flex-shrink-0 font-mono font-bold tabular-nums rounded-md border px-[0.45em] py-[0.1em] ${ton.texte} ${ton.bord}`}
            style={{ fontSize: petit ? '0.8em' : '0.85em' }}
        >
            ×{n}
        </span>
    );
}

/** Une catégorie : ses 5 premières questions, puis ce qui reste. */
function Categorie({ theme, ton, dense, retard }) {
    const visibles = theme.regroupements.slice(0, PAR_CATEGORIE);
    // On compte les QUESTIONS cachées, pas les lignes : une ligne « ×3 » en
    // cache trois, et c'est ce nombre-là que la salle attend.
    const cachees = theme.regroupements.slice(PAR_CATEGORIE).reduce(
        (s, g) => s + (g.n || separerCompteur(g.libelle).n || 1), 0);
    const taille = dense
        ? 'clamp(0.7rem, min(1.1vw, 2vh), 1.1rem)'
        : 'clamp(0.85rem, min(1.5vw, 2.7vh), 1.5rem)';

    return (
        <div
            className={`flex flex-col gap-[1.2vh] rounded-2xl border bg-white/[0.03] ${ton.bord} animate-fade-in-up`}
            style={{ padding: dense ? '1.6vh 1.3vw' : '2.2vh 1.8vw', animationDelay: `${retard}ms` }}
        >
            <div className="flex items-baseline justify-between gap-3 flex-shrink-0">
                <h2
                    className={`font-bold leading-tight ${ton.texte}`}
                    style={{ fontSize: dense
                        ? 'clamp(0.8rem, min(1.4vw, 2.5vh), 1.35rem)'
                        : 'clamp(0.95rem, min(1.9vw, 3.4vh), 1.85rem)' }}
                >
                    {theme.titre}
                </h2>
                <span
                    className="font-mono tabular-nums text-slate-400/60 whitespace-nowrap"
                    style={{ fontSize: 'clamp(0.6rem, min(1vw, 1.8vh), 1rem)' }}
                >
                    {theme.n}
                </span>
            </div>

            <ul className="flex flex-col gap-[1vh]">
                {visibles.map((g, i) => {
                    const { n, texte } = separerCompteur(g.libelle);
                    return (
                        <li key={i} className="flex items-start gap-[0.6em]">
                            <Compteur n={n || g.n} ton={ton} petit={dense} />
                            <span
                                className="text-slate-100/90 font-light leading-snug"
                                style={{ fontSize: taille }}
                            >
                                {texte}
                            </span>
                        </li>
                    );
                })}
            </ul>

            {cachees > 0 && (
                <p
                    className="font-mono text-slate-400/70"
                    style={{ fontSize: 'clamp(0.6rem, min(1vw, 1.8vh), 1rem)' }}
                >
                    + {cachees} autre{cachees > 1 ? 's' : ''} question{cachees > 1 ? 's' : ''}
                </p>
            )}
        </div>
    );
}

/** Les catégories seules — le repli d'un classement sans questions réécrites. */
function ThemesSeuls({ themes, ton }) {
    return (
        <div className="flex-1 min-h-0 flex flex-col justify-center gap-[3vh]">
            {themes.map((t, i) => (
                <div
                    key={i}
                    className="flex items-baseline gap-[2vw] animate-fade-in-up"
                    style={{ animationDelay: `${i * 180}ms` }}
                >
                    <span
                        className={`font-black tabular-nums flex-shrink-0 bg-gradient-to-r ${ton.titre} bg-clip-text text-transparent`}
                        style={{ fontSize: 'clamp(2rem, min(7vw, 14vh), 6rem)', minWidth: '2.2em', textAlign: 'right' }}
                    >
                        {t.n}
                    </span>
                    <span
                        className="text-slate-100/90 font-light leading-tight"
                        style={{ fontSize: 'clamp(1rem, min(2.6vw, 4.8vh), 2.5rem)' }}
                    >
                        {t.titre}
                    </span>
                </div>
            ))}
        </div>
    );
}

/* Une colonne par catégorie jusqu'à 3 ; à 4 (trois et « Autres »), une
   grille 2×2 — quatre colonnes de phrases de 20 mots seraient trop étroites
   pour se lire. */
const COLONNES = { 1: 'lg:grid-cols-1 max-w-5xl mx-auto w-full', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' };

const SalleQuestionsSlide = ({ slide, slideKey }) => {
    const ton = accent(slide.accent);
    // Une seule lecture, à l'affichage : l'écran ne bouge pas tout seul
    // pendant qu'Adrien répond. Même hook que la slide 34, même appel.
    const { resultat, erreur, chargement } = useSalleResultat();

    const themes = useMemo(
        () => (resultat?.themes || []).filter((t) => t && (t.titre || t.n)),
        [resultat],
    );

    const questions = themes.reduce((s, t) => s + (t.n || 0), 0);
    const lignes = themes.reduce((s, t) => s + Math.min(PAR_CATEGORIE, t.regroupements?.length || 0), 0);

    /* Aucune question réécrite nulle part : le classement date d'avant, ou le
       modèle ne les a pas rendues. On ne montre pas des cartes vides — les
       catégories seules disent déjà quelque chose. */
    const regime = lignes === 0 ? 'themes' : 'questions';
    const dense = lignes > LIGNES_CONFORTABLES || themes.length === 3;

    // Le repli du conducteur : pas d'écran d'erreur devant la salle, on rend
    // la parole. Identique à celui de la slide 34, volontairement.
    const vide = !chargement && themes.length === 0;

    return (
        <div key={slideKey} className="h-full flex flex-col px-16 pt-[2vh] pb-16 relative overflow-hidden">
            <div className="relative z-10 flex flex-col h-full gap-[2.5vh]">
                <div className="flex items-end justify-between gap-6 flex-shrink-0">
                    <div>
                        {slide.kicker && (
                            <p
                                className={`font-mono uppercase tracking-[0.3em] opacity-70 ${ton.texte}`}
                                style={{ fontSize: 'clamp(0.65rem, min(1.2vw, 2.2vh), 1.4rem)' }}
                            >
                                {slide.kicker}
                            </p>
                        )}
                        <h1
                            className={`font-black leading-[1.1] bg-gradient-to-r ${ton.titre} bg-clip-text text-transparent`}
                            style={{ fontSize: 'clamp(1.6rem, min(4vw, 8vh), 3.75rem)' }}
                        >
                            {slide.title || 'Vos questions'}
                        </h1>
                    </div>

                    {!vide && questions > 0 && (
                        <p
                            className="text-slate-400/70 font-mono tabular-nums whitespace-nowrap"
                            style={{ fontSize: 'clamp(0.7rem, min(1.2vw, 2.2vh), 1.25rem)' }}
                        >
                            {questions} question{questions > 1 ? 's' : ''} · {themes.length} thème{themes.length > 1 ? 's' : ''}
                            {erreur && <span className="ml-2 text-amber-400/70" title="Dernière lecture en échec">•</span>}
                        </p>
                    )}
                </div>

                {vide ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-[2vh]">
                        <p
                            className="text-slate-200 font-light leading-snug max-w-4xl"
                            style={{ fontSize: 'clamp(1.1rem, min(2.4vw, 4.4vh), 2.4rem)' }}
                        >
                            Vos questions, maintenant — à voix haute.
                        </p>
                        <p
                            className="text-slate-500 font-mono"
                            style={{ fontSize: 'clamp(0.7rem, min(1.1vw, 2vh), 1.1rem)' }}
                        >
                            {erreur ? 'Classement non lu' : 'Pas encore de classement'}
                        </p>
                    </div>
                ) : regime === 'themes' ? (
                    <ThemesSeuls themes={themes} ton={ton} />
                ) : (
                    /* `auto-rows-min` + `items-start` : deux catégories de
                       tailles différentes ne doivent pas étirer la carte la
                       plus courte sur toute la hauteur de la ligne.
                       `content-center` : avec peu de questions, les cartes
                       resteraient collées sous le titre et laisseraient la
                       moitié basse de l'écran vide — sur un mur, ça se lit
                       comme une slide inachevée. */
                    <div
                        className={`flex-1 min-h-0 overflow-hidden grid gap-[1.6vw] auto-rows-min items-start content-center grid-cols-1 ${COLONNES[themes.length] || 'lg:grid-cols-2'}`}
                    >
                        {themes.map((t, i) => (
                            <Categorie
                                key={i}
                                theme={{ ...t, regroupements: t.regroupements || [] }}
                                ton={ton}
                                dense={dense}
                                retard={i * 160}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SalleQuestionsSlide;
